package com.appsyde.shared.presentation.style

import org.jetbrains.compose.web.css.*

object AppStylesLanding : StyleSheet() {

    // ------------------------------------------------------------
    // Theme (calm / light)
    // ------------------------------------------------------------
    private const val BG = "#F7F8FC"              // off-white léger (pas blanc clinique)
    private const val TEXT = "#0F172A"            // slate-900
    private const val MUTED = "rgba(15,23,42,0.72)"
    private const val MUTED2 = "rgba(15,23,42,0.55)"
    private const val BORDER_SOFT = "rgba(15,23,42,0.10)"  // traits plus visibles sur fond clair
    private const val SURFACE = "rgba(255,255,255,0.75)"   // surface douce
    private const val SURFACE_SOLID = "#FFFFFF"

    val page by style {
        property(
            "font-family",
            // garde Inter (propre) mais on calme via weights plus bas partout
            "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial"
        )
        property("background", BG)
        property("color", TEXT)
        property("-webkit-font-smoothing", "antialiased")
        property("overflow-x", "hidden")
    }

    val container by style {
        width(100.percent)
        maxWidth(1160.px)
        property("margin-left", "auto")
        property("margin-right", "auto")
        paddingLeft(22.px)
        paddingRight(22.px)
    }

    /* ------------------------------ NAV ------------------------------ */

    val topNav by style {
        position(Position.Sticky)
        top(0.px)
        property("z-index", "50")
        property("background", "rgba(247,248,252,0.72)") // BG avec transparence
        property("backdrop-filter", "blur(10px)")
        property("border-bottom", "1px solid $BORDER_SOFT")
    }

    val navInner by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        paddingTop(14.px)
        paddingBottom(14.px)
        gap(12.px)
    }

    val navCtas by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
        media(mediaMaxWidth(920.px)) { self style { property("display", "none") } }
    }

    val landingNavCtasWrap by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Stretch)
        gap(12.px)
    }

    val landingCtaLabel by style {
        display(DisplayStyle.Block)
        property("font-size", "inherit")
        property("font-weight", "inherit")
    }

    val landingCtaSub by style {
        display(DisplayStyle.Block)
        fontSize(11.px)
        property("font-weight", "500")
        property("color", MUTED2)
        property("margin-top", "2px")
    }

    /** Bouton CTA avec label + sous-texte (landing nav et hero). */
    val landingCtaBtn by style {
        property("display", "inline-flex")
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.FlexStart)
        textAlign("left")
    }

    val brand by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
        fontSize(16.px)
        fontWeight(650) // un peu moins “loud”
        letterSpacing((-0.2).px)
        property("text-decoration", "none")
        property("color", TEXT)
    }

    val brandMark by style {
        width(28.px)
        height(28.px)
        borderRadius(10.px)
        // pas de couleur ici: tu as sûrement déjà un gradient via style ailleurs
    }

    /** Icône AppSyde à gauche du texte dans la nav / footer — petite taille */
    val brandIcon by style {
        height(16.px)
        property("width", "auto")
        property("max-height", "16px")
        property("object-fit", "contain")
        property("flex-shrink", "0")
        property("display", "block")
        borderRadius(4.px)
    }

    val navLinks by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(18.px)
        fontSize(14.px)
        property("color", MUTED2)
        property("white-space", "nowrap")
        media(mediaMaxWidth(920.px)) { self style { property("display", "none") } }
    }

    val navLink by style {
        property("color", MUTED2)
        property("text-decoration", "none")
        padding(8.px, 10.px)
        borderRadius(10.px)
        property("transition", "all 160ms ease")
        self + hover style {
            property("color", TEXT)
            property("background", "rgba(15,23,42,0.05)")
        }
    }

    val pill by style {
        fontSize(12.px)
        property("color", MUTED2)
        property("border", "1px solid $BORDER_SOFT")
        borderRadius(999.px)
        padding(7.px, 10.px)
        property("background", "rgba(255,255,255,0.55)")
        property("backdrop-filter", "blur(10px)")
    }

    /* ------------------------------ BUTTONS ------------------------------ */

    val btn by style {
        property("display", "inline-flex")
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(10.px)
        padding(13.px, 16.px)
        borderRadius(14.px)
        fontSize(14.px)
        fontWeight(640) // moins “agressif”
        property("border", "1px solid $BORDER_SOFT")
        property("background", "rgba(255,255,255,0.70)")
        property("color", TEXT)
        property("cursor", "pointer")
        property("text-decoration", "none")
        // micro-élévation soft (pas shiny)
        property("box-shadow", "0 6px 18px rgba(15,23,42,0.08)")
        property("transition", "transform 140ms ease, background 140ms ease, border 140ms ease, box-shadow 140ms ease")
        self + hover style {
            property("background", "rgba(255,255,255,0.88)")
            property("border", "1px solid rgba(15,23,42,0.14)")
            property("box-shadow", "0 10px 24px rgba(15,23,42,0.10)")
            property("transform", "translateY(-1px)")
        }
        self + active style { property("transform", "translateY(0)") }
    }

    val btnPrimary by style {
        // AppSyde accent, mais clean (pas néon)
        property("background", "rgba(102,96,230,0.10)")
        property("border", "1px solid rgba(102,96,230,0.28)")
        property("color", "#1E1B4B") // indigo dark (lisible sur fond clair)
        property("box-shadow", "0 10px 26px rgba(102,96,230,0.12)")
        self + hover style {
            property("background", "rgba(102,96,230,0.14)")
            property("border", "1px solid rgba(102,96,230,0.36)")
            property("box-shadow", "0 12px 30px rgba(102,96,230,0.14)")
        }
    }

    /* ------------------------------ HERO ------------------------------ */

    val hero by style {
        position(Position.Relative)
        paddingTop(88.px)
        paddingBottom(88.px)
        property("min-height", "78vh")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
    }

    val heroInner by style {
        width(100.percent)
        maxWidth(880.px)
        property("margin-left", "auto")
        property("margin-right", "auto")
    }

    val eyebrow by style {
        property("display", "inline-flex")
        alignItems(AlignItems.Center)
        gap(10.px)
        borderRadius(999.px)
        padding(9.px, 12.px)
        property("border", "1px solid $BORDER_SOFT")
        property("background", "rgba(255,255,255,0.60)")
        property("color", MUTED2)
        fontSize(13.px)
        fontWeight(560)
    }

    val dot by style {
        width(8.px)
        height(8.px)
        borderRadius(50.percent)
        property("background", "linear-gradient(135deg, rgba(102,96,230,0.85), rgba(96,188,217,0.75))")
        property("box-shadow", "0 0 0 rgba(0,0,0,0)") // pas de glow
    }

    val h1 by style {
        // un poil moins massif, plus “outil pro”
        fontSize(54.px)
        lineHeight("1.06")
        fontWeight(720)
        letterSpacing((-0.9).px)
        marginTop(18.px)
        marginBottom(16.px)
        media(mediaMaxWidth(920.px)) { self style { fontSize(44.px) } }
        media(mediaMaxWidth(520.px)) { self style { fontSize(34.px) } }
    }

    val gradientWord by style {
        property("background", "linear-gradient(135deg, rgba(102,96,230,0.95), rgba(96,188,217,0.85))")
        property("-webkit-background-clip", "text")
        property("-webkit-text-fill-color", "transparent")
        property("background-clip", "text")
        property("color", "transparent")
    }

    val subtitle by style {
        fontSize(18.px)
        lineHeight("1.62")
        property("color", MUTED)
        property("max-width", "62ch")
        marginBottom(22.px)
        fontWeight(480)
    }

    val heroCtas by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(12.px)
        flexWrap(FlexWrap.Wrap)
        marginTop(10.px)
    }

    val helperLine by style {
        marginTop(14.px)
        property("color", MUTED2)
        fontSize(13.px)
        lineHeight("1.5")
    }

    val heroSpacer by style {
        height(18.px)
        width(100.percent)
        property("background", "transparent")
    }

    /* ------------------------------ SECTIONS ------------------------------ */

    val section by style {
        paddingTop(72.px)
        paddingBottom(72.px)
    }

    val sectionHeader by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
        marginBottom(26.px)
    }

    val h2 by style {
        fontSize(30.px)
        fontWeight(720) // calmer
        letterSpacing((-0.5).px)
        lineHeight("1.2")
        margin(0.px)
        media(mediaMaxWidth(520.px)) { self style { fontSize(26.px) } }
    }

    val sectionLead by style {
        property("color", MUTED)
        fontSize(16.px)
        lineHeight("1.65")
        property("max-width", "76ch")
        margin(0.px)
        fontWeight(450)
    }

    val grid3 by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(3, 1fr)")
        gap(16.px)
        media(mediaMaxWidth(920.px)) { self style { property("grid-template-columns", "1fr") } }
    }

    val card by style {
        borderRadius(22.px)
        property("border", "1px solid $BORDER_SOFT")
        property("background", SURFACE)
        padding(22.px)
        // ombre soft (papier)
        property("box-shadow", "0 16px 40px rgba(15,23,42,0.08)")
        property("transition", "transform 140ms ease, border 140ms ease, background 140ms ease, box-shadow 140ms ease")
        self + hover style {
            property("transform", "translateY(-2px)")
            property("border", "1px solid rgba(15,23,42,0.14)")
            property("background", "rgba(255,255,255,0.85)")
            property("box-shadow", "0 20px 52px rgba(15,23,42,0.10)")
        }
    }

    val cardTitleRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
        marginBottom(10.px)
    }

    val iconBubble by style {
        width(34.px)
        height(34.px)
        borderRadius(12.px)
        property("background", "rgba(255,255,255,0.75)")
        property("border", "1px solid $BORDER_SOFT")
        property("display", "inline-flex")
        property("align-items", "center")
        property("justify-content", "center")
    }

    val cardTitle by style {
        fontSize(16.px)
        fontWeight(660) // calmer
        letterSpacing((-0.15).px)
        margin(0.px)
    }

    val cardBody by style {
        fontSize(15.px)
        lineHeight("1.7")
        property("color", MUTED)
        margin(0.px)
        fontWeight(450)
    }

    val divider by style {
        height(1.px)
        width(100.percent)
        property("background", BORDER_SOFT)
        marginTop(20.px)
        marginBottom(20.px)
    }

    val split by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "1fr 1fr")
        gap(16.px)
        media(mediaMaxWidth(920.px)) { self style { property("grid-template-columns", "1fr") } }
    }

    val steps by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
    }

    val step by style {
        borderRadius(20.px)
        property("border", "1px solid $BORDER_SOFT")
        property("background", SURFACE)
        padding(18.px)
        display(DisplayStyle.Flex)
        gap(12.px)
        alignItems(AlignItems.FlexStart)
        property("box-shadow", "0 12px 32px rgba(15,23,42,0.06)")
    }

    val stepNum by style {
        width(28.px)
        height(28.px)
        borderRadius(10.px)
        property("background", "linear-gradient(135deg, rgba(102,96,230,0.85), rgba(96,188,217,0.75))")
        property("display", "inline-flex")
        property("align-items", "center")
        property("justify-content", "center")
        fontSize(13.px)
        fontWeight(760) // moins “punchy”
        flexShrink(0)
        property("color", SURFACE_SOLID)
    }

    val stepTitle by style {
        fontWeight(760)
        marginBottom(6.px)
        letterSpacing((-0.2).px)
    }

    val cardCtaWrap by style {
        marginTop(14.px)
    }

    val finalCtaCard by style {
        padding(24.px)
    }

    val footerInner by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center)
        gap(12.px)
        flexWrap(FlexWrap.Wrap)
    }

    val footerBrandRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
    }

    val footerLinks by style {
        display(DisplayStyle.Flex)
        gap(14.px)
        flexWrap(FlexWrap.Wrap)
    }

    val footerDisclaimer by style {
        marginTop(10.px)
    }

    /* ------------------------------ FAQ ------------------------------ */

    /** Contenu des pages légales (Privacy, Terms, Legal) : espacement entre paragraphes */
    val legalContent by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(16.px)
    }

    /** Sous-titre de section dans les pages légales (ex. ## 1. Information We Collect) */
    val legalH2 by style {
        fontSize(20.px)
        fontWeight(650)
        marginTop(24.px)
        marginBottom(8.px)
        margin(0.px)
        property("color", TEXT)
    }

    /** Sous-sous-titre (ex. ### Account Information) */
    val legalH3 by style {
        fontSize(16.px)
        fontWeight(600)
        marginTop(16.px)
        marginBottom(6.px)
        margin(0.px)
        property("color", TEXT)
    }

    /** Liste à puces dans les pages légales */
    val legalList by style {
        property("margin", "0 0 12px 0")
        paddingLeft(24.px)
    }

    val legalListItem by style {
        marginBottom(6.px)
    }

    /** Date de dernière mise à jour */
    val legalUpdated by style {
        fontSize(13.px)
        property("color", MUTED2)
        marginBottom(20.px)
    }

    val faq by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
    }

    val faqItem by style {
        borderRadius(20.px)
        property("border", "1px solid $BORDER_SOFT")
        property("background", SURFACE)
        padding(18.px)
        property("box-shadow", "0 12px 32px rgba(15,23,42,0.06)")
    }

    val faqQ by style {
        fontWeight(650)
        marginBottom(6.px)
    }

    /* ------------------------------ FOOTER ------------------------------ */

    val footer by style {
        paddingTop(38.px)
        paddingBottom(50.px)
        property("border-top", "1px solid $BORDER_SOFT")
        property("color", MUTED2)
        fontSize(13.px)
    }

    /* ------------------------------ AURORA (very subtle) ------------------------------ */

    val aurora by style {
        position(Position.Absolute)
        property("inset", "0")
        property("pointer-events", "none")
        property("overflow", "hidden")
        opacity(0.55) // plus discret
    }

    val blob1 by style {
        position(Position.Absolute)
        top((-240).px)
        left((-240).px)
        width(720.px)
        height(720.px)
        borderRadius(50.percent)
        property("background", "radial-gradient(circle, rgba(102,96,230,0.16), transparent 70%)")
        property("filter", "blur(28px)")
        opacity(0.95)
    }

    val blob2 by style {
        position(Position.Absolute)
        bottom((-260).px)
        right((-260).px)
        width(760.px)
        height(760.px)
        borderRadius(50.percent)
        property("background", "radial-gradient(circle, rgba(96,188,217,0.12), transparent 70%)")
        property("filter", "blur(30px)")
        opacity(0.95)
    }
}
